import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { VendorProtectedRoute } from "./components/VendorProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";

// Only import login/activate pages eagerly (needed before auth)
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { ActivateAccountPage } from "./pages/ActivateAccountPage";
import { InviteAcceptancePage } from "./pages/InviteAcceptancePage";
import { PendingUsersPage } from "./pages/PendingUsersPage";
import { UnauthorizedPage } from "./pages/UnauthorizedPage";
import SSOCallbackPage from "./pages/SSOCallbackPage";

// User Role Management
const UserRoleManagementPage = lazy(() =>
  import("./pages/UserRoleManagementPage").then((m) => ({
    default: m.UserRoleManagementPage,
  }))
);

// Public RTR pages
const EmployeeRtrSigningPage = lazy(() =>
  import("./pages/public/EmployeeRtrSigningPage").then((m) => ({
    default: m.default,
  }))
);
const EmployerRtrSigningPage = lazy(() =>
  import("./pages/public/EmployerRtrSigningPage").then((m) => ({
    default: m.default,
  }))
);

// Public Landing & Legal pages
const LandingPage = lazy(() =>
  import("./pages/public/LandingPage").then((m) => ({
    default: m.default,
  }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/public/PrivacyPolicyPage").then((m) => ({
    default: m.default,
  }))
);
const TermsOfServicePage = lazy(() =>
  import("./pages/public/TermsOfServicePage").then((m) => ({
    default: m.default,
  }))
);
const AboutPage = lazy(() =>
  import("./pages/public/AboutPage").then((m) => ({
    default: m.default,
  }))
);
const CareersPage = lazy(() =>
  import("./pages/public/CareersPage").then((m) => ({
    default: m.default,
  }))
);

// Lazy load all protected pages
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage }))
);
// Role-based dashboard (new implementation)
const RoleBasedDashboard = lazy(() =>
  import("./components/dashboard/RoleBasedDashboard").then((m) => ({ default: m.default }))
);
// Teams page for team breakdown view
const TeamsPage = lazy(() =>
  import("./pages/TeamsPage").then((m) => ({ default: m.TeamsPage }))
);
// My Work page for manager's personal work
const MyWorkPage = lazy(() =>
  import("./pages/MyWorkPage").then((m) => ({ default: m.MyWorkPage }))
);
const SupportPage = lazy(() =>
  import("./pages/SupportPage").then((m) => ({ default: m.SupportPage }))
);
const TenantDetailsPage = lazy(() =>
  import("./pages/TenantDetailsPage").then((m) => ({
    default: m.TenantDetailsPage,
  }))
);

// Tenants Module
const TenantsListPage = lazy(() =>
  import("./modules/tenants").then((m) => ({ default: m.TenantsListPage }))
);
const CreateTenantWizard = lazy(() =>
  import("./modules/tenants").then((m) => ({ default: m.CreateTenantWizard }))
);
const TenantSettingsPage = lazy(() =>
  import("./modules/tenants").then((m) => ({ default: m.TenantSettingsPage }))
);
const TenantUsersPage = lazy(() =>
  import("./modules/tenants").then((m) => ({ default: m.TenantUsersPage }))
);
const TenantRolesPage = lazy(() =>
  import("./modules/tenants").then((m) => ({ default: m.TenantRolesPage }))
);

// Vendors Module
const VendorsListPage = lazy(() =>
  import("./modules/vendors").then((m) => ({ default: m.VendorsListPage }))
);
const VendorDetailPage = lazy(() =>
  import("./modules/vendors").then((m) => ({ default: m.VendorDetailPage }))
);
const VendorCreateEditPage = lazy(() =>
  import("./modules/vendors").then((m) => ({ default: m.VendorCreateEditPage }))
);
const VendorUsersPage = lazy(() =>
  import("./modules/vendors").then((m) => ({ default: m.VendorUsersPage }))
);

// Requisitions Module
const RequisitionsListPage = lazy(() =>
  import("./modules/requisitions").then((m) => ({
    default: m.RequisitionsListPageModern,
  }))
);
const RequisitionDetailPage = lazy(() =>
  import("./modules/requisitions").then((m) => ({
    default: m.RequisitionDetailPage,
  }))
);
const RequisitionCreateEditPage = lazy(() =>
  import("./modules/requisitions").then((m) => ({
    default: m.RequisitionCreateEditPage,
  }))
);
const RequisitionCreateWizard = lazy(() =>
  import("./modules/requisitions").then((m) => ({
    default: m.RequisitionCreateWizard,
  }))
);
const VendorRequisitionsPage = lazy(() =>
  import("./modules/requisitions").then((m) => ({
    default: m.VendorRequisitionsPage,
  }))
);
const VendorRequisitionDetailPage = lazy(() =>
  import("./modules/requisitions").then((m) => ({
    default: m.VendorRequisitionDetailPage,
  }))
);

// Candidates Module
const CandidatesListPage = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.CandidatesListPageModern,
  }))
);
const BulkCandidateImportPage = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.BulkCandidateImportPage,
  }))
);
const CandidateCreateWizard = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.CandidateCreateWizard,
  }))
);
const CandidateDetailPage = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.CandidateDetailPage,
  }))
);
const CandidateCreateEditPage = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.CandidateCreateEditPage,
  }))
);
const VendorCandidatesModulePage = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.VendorCandidatesPage,
  }))
);
const VendorCandidateDetailPage = lazy(() =>
  import("./modules/candidates").then((m) => ({
    default: m.VendorCandidateDetailPage,
  }))
);

// Data Hub Module
const DataHubDashboard = lazy(() =>
  import("./modules/data-hub").then((m) => ({ default: m.DataHubDashboard }))
);
const CandidateImportsListPage = lazy(() =>
  import("./modules/data-hub").then((m) => ({
    default: m.CandidateImportsListPage,
  }))
);
const CandidateImportWizard = lazy(() =>
  import("./modules/data-hub").then((m) => ({
    default: m.CandidateImportWizard,
  }))
);
const RequisitionsImportsListPage = lazy(() =>
  import("./modules/data-hub").then((m) => ({
    default: m.RequisitionsImportsListPage,
  }))
);
const RequisitionImportWizard = lazy(() =>
  import("./modules/data-hub").then((m) => ({
    default: m.RequisitionImportWizard,
  }))
);

// Submissions Module
const SubmissionsListPage = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.SubmissionsListPage,
  }))
);
const SubmissionCreateWizard = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.SubmissionCreateWizard,
  }))
);
const SubmissionDetailPage = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.SubmissionDetailPage,
  }))
);
const VendorSubmissionsListPage = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.VendorSubmissionsListPage,
  }))
);
const VendorSubmissionDetailPage = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.VendorSubmissionDetailPage,
  }))
);
const SubmissionDashboard = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.SubmissionDashboard,
  }))
);
const SubmissionKanbanView = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.SubmissionKanbanView,
  }))
);
const SubmissionTimelineView = lazy(() =>
  import("./modules/submissions").then((m) => ({
    default: m.SubmissionTimelineView,
  }))
);

// Vendor Portal Module
const VendorDashboardPage = lazy(() =>
  import("./modules/vendor-portal").then((m) => ({
    default: m.VendorDashboardPage,
  }))
);
const VendorSettingsPage = lazy(() =>
  import("./modules/vendor-portal").then((m) => ({
    default: m.VendorSettingsPage,
  }))
);

// Analytics Module
const AnalyticsPage = lazy(
  () => import("./modules/analytics/pages/AnalyticsPage")
);
const CalendarDrilldownPage = lazy(
  () => import("./modules/analytics/pages/CalendarDrilldownPage")
);

// Profile Module
const ProfilePage = lazy(() =>
  import("./modules/profile").then((m) => ({ default: m.ProfilePage }))
);

// Settings Module
const SettingsHubPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.SettingsHubPage }))
);
const MetadataManagementPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.MetadataManagementPage,
  }))
);
const MetadataFormPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.MetadataFormPage }))
);
const IntegrationsManagementPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.IntegrationsManagementPage,
  }))
);
const IntegrationConfigPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.IntegrationConfigPage,
  }))
);
const StorageManagementPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.StorageManagementPage,
  }))
);
const NewStorageConfigPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.NewStorageConfigPage,
  }))
);
const EditStorageConfigPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.EditStorageConfigPage,
  }))
);
const EmailManagementPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.EmailManagementPage }))
);
const NewEmailConfigPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.NewEmailConfigPage }))
);
const NotificationsManagementPage = lazy(() =>
  import("./modules/settings").then((m) => ({
    default: m.NotificationsManagementPage,
  }))
);
const GeneralSettingsPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.GeneralSettingsPage }))
);
const ImportSettingsPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.ImportSettingsPage }))
);
const SecurityAuditPage = lazy(() =>
  import("./modules/settings").then((m) => ({ default: m.SecurityAuditPage }))
);
const SystemRolesPage = lazy(() =>
  import("./modules/settings/pages/SystemRolesPage").then((m) => ({
    default: m.SystemRolesPage,
  }))
);

// Notifications Module
const NotificationTemplatesPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.NotificationTemplatesPage,
  }))
);
const TemplateEditorPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.TemplateEditorPage,
  }))
);
const NotificationRulesPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.NotificationRulesPage,
  }))
);
const NotificationListsPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.NotificationListsPage,
  }))
);
const DistributionListsPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.DistributionListsPage,
  }))
);
const NotificationChannelsPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.NotificationChannelsPage,
  }))
);
const NotificationJobsPage = lazy(() =>
  import("./pages/notifications").then((m) => ({
    default: m.NotificationJobsPage,
  }))
);

// AI Insights
const AIInsightsPage = lazy(() =>
  import("./pages/AIInsightsPage").then((m) => ({ default: m.AIInsightsPage }))
);

// Automation & AI Dashboard Module
const AutomationDashboard = lazy(() =>
  import("./components/automation/AutomationDashboard").then((m) => ({
    default: m.default,
  }))
);
const PredictiveAnalyticsDashboard = lazy(() =>
  import("./components/automation/PredictiveAnalyticsDashboard").then((m) => ({
    default: m.default,
  }))
);
const AICoachingDashboard = lazy(() =>
  import("./components/automation/AICoachingDashboard").then((m) => ({
    default: m.default,
  }))
);
const AutomationRulesManager = lazy(() =>
  import("./components/automation/AutomationRulesManager").then((m) => ({
    default: m.default,
  }))
);

// Performance Management
const PerformanceManagement = lazy(() =>
  import("./pages/PerformanceManagement").then((m) => ({
    default: m.default,
  }))
);

// Organizational Management
const OrganizationalManagement = lazy(() =>
  import("./pages/OrganizationalManagement").then((m) => ({
    default: m.default,
  }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30 * 1000, // 30 seconds - data stays fresh
      gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache
      refetchOnMount: 'always', // Refetch if stale on mount
      refetchOnReconnect: true, // Refetch when network reconnects
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#1f2937",
                    color: "#fff",
                    border: "1px solid #374151",
                  },
                  success: {
                    iconTheme: {
                      primary: "#10b981",
                      secondary: "#fff",
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: "#ef4444",
                      secondary: "#fff",
                    },
                  },
                }}
              />
              <AppRoutes />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading...</p>
          </div>
        </div>
      }
    >
      <Routes>
        {/* Public Landing & Legal Pages */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/careers" element={<CareersPage />} />
        
        {/* Auth Pages */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/activate-account" element={<ActivateAccountPage />} />
        <Route path="/activate" element={<ActivateAccountPage />} />
        <Route path="/invite" element={<InviteAcceptancePage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/sso-callback" element={<SSOCallbackPage />} />

        {/* Public RTR Signing Routes */}
        <Route
          path="/rtr/employee/:token"
          element={<EmployeeRtrSigningPage />}
        />
        <Route
          path="/rtr/employer/:token"
          element={<EmployerRtrSigningPage />}
        />

        {/* Main Portal Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<RoleBasedDashboard />} />
          <Route path="/dashboard/legacy" element={<DashboardPage />} />
          <Route path="/teams" element={<TeamsPage />} />
          <Route path="/my-work" element={<MyWorkPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/analytics/calendar/:date/drilldown"
            element={<CalendarDrilldownPage />}
          />

          {/* Admin Routes */}
          <Route path="/admin/pending-users" element={<PendingUsersPage />} />

          {/* Tenants Module (new implementation) */}
          <Route path="/tenants" element={<TenantsListPage />} />
          <Route path="/tenants/new" element={<CreateTenantWizard />} />
          <Route path="/tenants/:id" element={<TenantDetailsPage />} />

          {/* Vendors Module (new implementation) */}
          <Route
            path="/vendors"
            element={
              <VendorsListPage user={user!} tenantId={user?.tenantId || ""} />
            }
          />
          <Route
            path="/vendors/new"
            element={
              <VendorCreateEditPage
                user={user!}
                tenantId={user?.tenantId || ""}
              />
            }
          />
          <Route
            path="/vendors/:vendorId"
            element={<VendorDetailPage user={user!} />}
          />
          <Route
            path="/vendors/:vendorId/edit"
            element={
              <VendorCreateEditPage
                user={user!}
                tenantId={user?.tenantId || ""}
              />
            }
          />
          <Route
            path="/vendors/:vendorId/users"
            element={
              <VendorUsersPage user={user!} tenantId={user?.tenantId || ""} />
            }
          />

          {/* Requisitions Module (new implementation) */}
          <Route path="/requisitions" element={<RequisitionsListPage />} />
          <Route
            path="/requisitions/new"
            element={<RequisitionCreateWizard />}
          />
          <Route path="/requisitions/:id" element={<RequisitionDetailPage />} />
          <Route
            path="/requisitions/:id/edit"
            element={<RequisitionCreateEditPage />}
          />

          {/* Candidates Module (new implementation) */}
          <Route path="/candidates" element={<CandidatesListPage />} />
          <Route path="/candidates/new" element={<CandidateCreateWizard />} />
          <Route path="/candidates/:id" element={<CandidateDetailPage />} />
          <Route
            path="/candidates/:id/edit"
            element={<CandidateCreateEditPage />}
          />

          {/* Data Hub Module */}
          <Route path="/data-hub" element={<DataHubDashboard />} />
          <Route
            path="/data-hub/candidates"
            element={<CandidateImportsListPage />}
          />
          <Route
            path="/data-hub/candidates/import/:batchId"
            element={<CandidateImportWizard />}
          />
          <Route
            path="/data-hub/requisitions"
            element={<RequisitionsImportsListPage />}
          />
          <Route
            path="/data-hub/requisitions/import/:batchId"
            element={<RequisitionImportWizard />}
          />
          <Route
            path="/data-hub/import-candidates"
            element={<BulkCandidateImportPage />}
          />

          {/* Submissions Module (new implementation) */}
          <Route path="/submissions" element={<SubmissionDashboard />} />
          <Route path="/submissions/list" element={<SubmissionsListPage />} />
          <Route
            path="/submissions/kanban"
            element={<SubmissionKanbanView />}
          />
          <Route
            path="/submissions/timeline"
            element={<SubmissionTimelineView />}
          />
          <Route path="/submissions/new" element={<SubmissionCreateWizard />} />
          <Route
            path="/submissions/create"
            element={<SubmissionCreateWizard />}
          />
          <Route path="/submissions/:id" element={<SubmissionDetailPage />} />

          {/* AI Insights */}
          <Route path="/ai-insights" element={<AIInsightsPage />} />

          {/* Performance Management */}
          <Route path="/performance" element={<PerformanceManagement />} />

          {/* Organizational Management */}
          <Route path="/organizational" element={<OrganizationalManagement />} />

          {/* AI Automation & Performance Management */}
          <Route path="/automation" element={<AutomationDashboard />} />
          <Route
            path="/automation/predictions"
            element={<PredictiveAnalyticsDashboard />}
          />
          <Route
            path="/automation/coaching"
            element={<AICoachingDashboard />}
          />
          <Route
            path="/automation/rules"
            element={<AutomationRulesManager />}
          />

          {/* Settings Module */}
          <Route path="/settings" element={<SettingsHubPage />} />
          <Route
            path="/settings/metadata"
            element={<MetadataManagementPage />}
          />
          <Route path="/settings/metadata/new" element={<MetadataFormPage />} />
          <Route
            path="/settings/metadata/:id/edit"
            element={<MetadataFormPage />}
          />
          <Route
            path="/settings/integrations"
            element={<IntegrationsManagementPage />}
          />
          <Route
            path="/settings/integrations/new/:provider"
            element={<IntegrationConfigPage />}
          />
          <Route
            path="/settings/integrations/:id"
            element={<IntegrationConfigPage />}
          />
          <Route path="/settings/storage" element={<StorageManagementPage />} />
          <Route
            path="/settings/storage/new"
            element={<NewStorageConfigPage />}
          />
          <Route
            path="/settings/storage/:id"
            element={<EditStorageConfigPage />}
          />
          <Route path="/settings/email" element={<EmailManagementPage />} />
          <Route path="/settings/email/new" element={<NewEmailConfigPage />} />
          <Route path="/settings/email/:id" element={<NewEmailConfigPage />} />
          <Route
            path="/settings/notifications"
            element={<NotificationsManagementPage />}
          />
          <Route path="/settings/general" element={<GeneralSettingsPage />} />
          <Route path="/settings/import" element={<ImportSettingsPage />} />
          <Route path="/settings/security" element={<SecurityAuditPage />} />
          <Route path="/settings/user-roles" element={<SystemRolesPage />} />

          {/* Notifications Management */}
          <Route
            path="/notifications"
            element={<Navigate to="/notifications/lists" replace />}
          />
          <Route
            path="/notifications/templates"
            element={<NotificationTemplatesPage />}
          />
          <Route
            path="/notifications/templates/new"
            element={<TemplateEditorPage />}
          />
          <Route
            path="/notifications/templates/:id/edit"
            element={<TemplateEditorPage />}
          />
          <Route
            path="/notifications/rules"
            element={<NotificationRulesPage />}
          />
          <Route
            path="/notifications/lists"
            element={<DistributionListsPage />}
          />
          <Route
            path="/notifications/distribution-lists"
            element={<DistributionListsPage />}
          />
          <Route
            path="/notifications/channels"
            element={<NotificationChannelsPage />}
          />
          <Route
            path="/notifications/jobs"
            element={<NotificationJobsPage />}
          />

          <Route path="/settings/tenant" element={<TenantSettingsPage />} />

          <Route path="/settings/users" element={<TenantUsersPage />} />
          <Route path="/settings/roles" element={<TenantRolesPage />} />
          <Route
            path="/settings/user-roles"
            element={<UserRoleManagementPage />}
          />

          {/* Profile */}
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* Vendor Portal Routes (with separate layout) */}
        <Route element={<VendorProtectedRoute />}>
          <Route path="/vendor/dashboard" element={<VendorDashboardPage />} />
          <Route
            path="/vendor/requisitions"
            element={<VendorRequisitionsPage />}
          />
          <Route
            path="/vendor/requisitions/:id"
            element={<VendorRequisitionDetailPage />}
          />
          <Route
            path="/vendor/candidates"
            element={<VendorCandidatesModulePage />}
          />
          <Route
            path="/vendor/candidates/new"
            element={<CandidateCreateEditPage />}
          />
          <Route
            path="/vendor/candidates/:id"
            element={<VendorCandidateDetailPage />}
          />
          <Route
            path="/vendor/candidates/:id/edit"
            element={<CandidateCreateEditPage />}
          />
          <Route
            path="/vendor/submissions"
            element={<VendorSubmissionsListPage />}
          />
          <Route
            path="/vendor/submissions/:id"
            element={<VendorSubmissionDetailPage />}
          />
          <Route
            path="/vendor/settings/profile"
            element={<VendorSettingsPage />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
